/**
 * openList.js — the Today board's ONE open list and its cycles.
 *
 * Items stay on the board until someone moves the list to History (`archived`); a new
 * cycle starts at closing time (LIST_CYCLE_HOUR), from when new notes are for tomorrow.
 */

import { LIST_CYCLE_HOUR, LIST_CYCLES_SINCE } from '../config/constants';
import { STATUS } from '../config/lists';
import { localDateISO } from './dates';
import { itemStatus } from './items';

/**
 * The day a list is FOR. From LIST_CYCLE_HOUR (closing time) on, new notes are for
 * tomorrow — so Tue 9 PM → Wed, Wed 10 AM → Wed, Sat 12:30 AM → Sat. Calendar
 * arithmetic (not +N hours) so DST days stay correct.
 */
export function listDayISO(d = new Date()) {
  const x = new Date(d);
  if (x.getHours() >= LIST_CYCLE_HOUR) x.setDate(x.getDate() + 1);
  return localDateISO(x);
}

/** Which list-day an item belongs to: `listFor` (new items) or its noted date (legacy). */
export const listDayOf = item => (item && (item.listFor || item.date)) || '';

/**
 * Is the item on the open board list? Items stay until moved to History (`archived`) —
 * never dropped by the clock. Legacy items (no `listFor`: saved before list cycles, or
 * by an iPad still on the old build) count as open from LIST_CYCLES_SINCE on; anything
 * older was already history under the old midnight rule.
 */
export function isOpenItem(item) {
  if (!item || item.archived) return false;
  if (item.listFor) return true;
  return (item.date || '') >= LIST_CYCLES_SINCE;
}

/**
 * Milliseconds until the board's view of time next changes — the next list-cycle start
 * (LIST_CYCLE_HOUR) or midnight, whichever comes first — so an idle iPad re-renders
 * exactly then (stale nudge, "List for …", closing reminder) instead of on next touch.
 */
export function msUntilNextBoundary(now = new Date()) {
  const next = new Date(now);
  next.setHours(now.getHours() >= LIST_CYCLE_HOUR ? 24 : LIST_CYCLE_HOUR, 0, 0, 0);
  return Math.max(1000, next.getTime() - now.getTime());
}

/**
 * State of the open list: `curDay` = the list-day right now; `listDay` = the day the
 * board's list is for (its newest item's); `firstDay` = its oldest; `stale` = a new
 * cycle has begun and nobody has added to the list in it yet → the next add asks
 * "move it to History, or keep adding?".
 */
export function boardState(open, now = new Date()) {
  const curDay = listDayISO(now);
  const days = (open || []).map(listDayOf).filter(Boolean).sort();
  if (!days.length) return { curDay, listDay: curDay, firstDay: curDay, stale: false, count: 0 };
  const listDay = days[days.length - 1];
  return { curDay, listDay, firstDay: days[0], stale: listDay < curDay, count: days.length };
}

/**
 * Split an open list for "move to History": unfinished (still `needed`) items noted
 * today vs earlier. Done + flagged items always go to History (flagged ones stay on
 * the Review tab for sourcing regardless).
 */
export function splitOpenList(open, todayISO = localDateISO()) {
  const unfinished = (open || []).filter(i => itemStatus(i) === STATUS.NEEDED);
  return {
    recent: unfinished.filter(i => i.date === todayISO),
    older: unfinished.filter(i => i.date !== todayISO),
    done: (open || []).filter(i => itemStatus(i) !== STATUS.NEEDED),
  };
}
